import classNames from "classnames";
import React, { useContext } from "react";
import styled from "styled-components";
import BreakpointContext from "../../contexts/breakpointContext";
import DraftDispatchContext from "../../contexts/draftDispatchContext";
import DraftStateContext from "../../contexts/draftStateContext";
import useDraftPicks from "../../hooks/queryResults/draft/useDraftPicks";
import useNflTeams from "../../hooks/queryResults/nfl/useNflTeams";
import useAllPlayers from "../../hooks/queryResults/useAllPlayers";
import useDraftMutations from "../../hooks/queryResults/draft/useDraftMutations";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useSiteRankings from "../../hooks/queryResults/useSiteRankings";
import useTeams from "../../hooks/queryResults/useTeams";
import useCurrentUser from "../../hooks/useCurrentUser";
import Util from "../../util/util";
import PlayerImageByPos from "../player/PlayerImageByPos";
import { PosPlayerNameTeam } from "../player/PlayerName";
import Loading from "../ui/Loading";
import { DRAFT_ACTIONS } from "./draftReducer";
import WatchIcon from "./WatchIcon";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";
import { arrayMoveImmutable } from "array-move";

const StyledTable = styled.table`
  width: 100%;
  padding: 0;

  & tr {
    cursor: pointer;
  }

  & td {
    padding: 0.5rem 0.2rem;
  }

  & tr.taken,
  & tr.taken:hover td {
    background-color: #ddd;
    color: #aaa;
  }

  & tr.taken:nth-child(odd),
  & tr.taken:nth-child(odd):hover {
    background-color: #ccc;
    color: #888;
  }

  & tr.selected td,
  & tr.selected:hover td {
    background-color: #0078f8;
    color: white;
  }

  & tr.selected.on-clock td,
  & tr.selected.on-clock:hover td {
    background-color: #00ab00;
    color: white;
  }

  & tr.selected.on-clock.time-running-out td,
  & tr.selected.on-clock.time-running-out:hover td {
    background-color: #ff0000;
    color: white;
  }
`;

export default function WatchList() {
  const breakpoints = useContext(BreakpointContext);
  const draftState = useContext(DraftStateContext);
  const draftDispatch = useContext(DraftDispatchContext);
  const currentUser = useCurrentUser();

  const { draftPicksData, draftPicksQuery } = useDraftPicks();
  const { nflTeamsData, nflTeamsQuery } = useNflTeams();
  const { playersData, playersQuery } = useAllPlayers();
  const { siteRankingsData, siteRankingsQuery } = useSiteRankings();
  const { teams, teamsQuery } = useTeams();

  const { updateWatchListMutation } = useDraftMutations();
  const { selectedPlayerId, watchList } = draftState;

  const isLoading = useIsLoading(
    draftPicksData,
    draftPicksQuery,
    playersData,
    playersQuery,
    siteRankingsData,
    siteRankingsQuery,
    teams,
    teamsQuery,
    nflTeamsData,
    nflTeamsQuery
  );

  if (isLoading) return <Loading />;

  const availablePlayerIds = watchList.filter(
    (playerId) => !draftPicksData.players.hasOwnProperty(playerId)
  );

  const { teamIdOnClock, timeIsRunningOut } = draftState.info;

  const teamOnClock = teamIdOnClock ? teams.getTeamById(teamIdOnClock) : null;
  let userIsOnClock = Util.hasDraftManagerRole(currentUser, teamOnClock);

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    const newWatchList = arrayMoveImmutable(
      availablePlayerIds,
      oldIndex,
      newIndex
    );
    console.log(newWatchList);
    draftDispatch({
      type: DRAFT_ACTIONS.REORDER_WATCH_LIST,
      payload: newWatchList,
    });
    updateWatchListMutation.mutate(newWatchList);
  };

  const DragHandle = SortableHandle(() => (
    <td style={{ cursor: "row-resize", paddingRight: "0.5rem" }}>:::</td>
  ));

  const SortableItem = SortableElement(({ value: playerId }) => {
    const player = playersData.players[playerId];
    let isWatching = watchList.includes(playerId);

    const classNamesObj = {};
    let isAvailable = !draftPicksData.players.hasOwnProperty(playerId);
    classNamesObj.taken = !isAvailable;

    const isSelected = selectedPlayerId === playerId;
    if (isSelected) {
      classNamesObj.selected = isSelected;
      classNamesObj["on-clock"] = userIsOnClock;
      classNamesObj["time-running-out"] = userIsOnClock && timeIsRunningOut;
    }

    const isTaken = (playerId) => {
      return draftPicksData.players.hasOwnProperty(playerId);
    };

    const toggleWatching = (event) => {
      event.stopPropagation();
      let newWatchList;
      if (isWatching) {
        draftDispatch({
          type: DRAFT_ACTIONS.REMOVE_FROM_WATCH_LIST,
          payload: playerId,
        });
        newWatchList = watchList.filter((pid) => pid !== playerId);
      } else {
        draftDispatch({
          type: DRAFT_ACTIONS.ADD_TO_WATCH_LIST,
          payload: playerId,
        });
        newWatchList = [...watchList, playerId];
      }
      isWatching = !isWatching;

      newWatchList = newWatchList.filter((playerId) => !isTaken(playerId));
      updateWatchListMutation.mutate(newWatchList);
    };

    const handleClick = () => {
      if (isAvailable)
        draftDispatch({
          type: DRAFT_ACTIONS.SELECT_PLAYER_ID,
          payload: playerId,
        });
    };

    const rank =
      Object.values(siteRankingsData.playerList.entries).indexOf(playerId) + 1;

    let byeWeek = null;
    if (player.nflTeamAbbr) {
      const nflTeam = nflTeamsData.nflTeams[player.nflTeamAbbr];
      if (nflTeam) byeWeek = nflTeam.byeWeek;
    }

    return (
      <tr className={classNames(classNamesObj)} onClick={handleClick}>
        <DragHandle />
        <td>
          <WatchIcon
            isWatching={isWatching}
            toggleWatching={toggleWatching}
            isSelected={isSelected}
          />
        </td>
        <td title={"Rank"}>{rank}</td>
        {breakpoints.lg ? (
          <td>
            <PlayerImageByPos player={player} style={{ maxWidth: "18px" }} />
          </td>
        ) : null}
        <td className={"left"} style={{ width: "12rem" }}>
          <PosPlayerNameTeam player={player} />
          {byeWeek ? (
            <span title={"Bye Week"} style={{ paddingLeft: "0.25rem" }}>
              ({byeWeek})
            </span>
          ) : null}
        </td>
      </tr>
    );
  });

  const SortableList = SortableContainer(({ items }) => {
    return (
      <StyledTable>
        <tbody>
          {items.map((value, index) => (
            <SortableItem key={`item-${value}`} index={index} value={value} />
          ))}
        </tbody>
      </StyledTable>
    );
  });

  const height = breakpoints.md ? "auto" : "15rem";
  const overflowY = breakpoints.md ? "auto" : "scroll";

  return (
    <div style={{ height, overflowY }}>
      {availablePlayerIds.length ? (
        <SortableList
          items={availablePlayerIds}
          onSortEnd={handleSortEnd}
          useDragHandle
        />
      ) : (
        <p style={{ textAlign: "center", color: "#999999" }}>
          Watch List is empty. Add players by clicking on the eyeball icon next
          to their names.
        </p>
      )}
    </div>
  );
}
